import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthFetch } from './useAuthFetch';
import type { VoiceSettings, VoiceStatus, Persona, TokenResponse, TranscriptionJob, UploadTicket } from '@/types';

/** Poll fast while something is mid-transition, slowly once state is settled. */
const POLL_TRANSITIONING_MS = 5_000;
const POLL_SETTLED_MS = 30_000;

function isTransitioning(s: VoiceStatus | undefined): boolean {
  if (!s) return true; // unknown yet — find out promptly
  const settled = (v: string) => v === 'InService' || v === 'NotFound' || v === 'Failed';
  return !settled(s.asr) || !settled(s.tts) || (s.livekit !== 'running' && s.livekit !== 'stopped');
}

export function useVoiceStatus() {
  const authFetch = useAuthFetch();
  return useQuery<VoiceStatus>({
    queryKey: ['voice', 'status'],
    queryFn: async () => {
      const res = await authFetch('/v1/voice/status');
      if (!res.ok) throw new Error('Failed to fetch status');
      return res.json();
    },
    // Adaptive interval instead of a flat 5s.
    //
    // Each poll costs TWO SageMaker DescribeEndpoint calls (ASR + TTS), and those
    // share a low account-level rate limit with DeleteEndpoint. A flat 5s poll is
    // ~24 SageMaker calls/minute per open tab, sustained even when nothing is
    // happening — which is what pushed the account over the limit and made
    // POST /v1/voice/stop fail with ThrottlingException about 1 call in 10.
    //
    // Endpoints take minutes to come up, so 5s only buys responsiveness while a
    // transition is actually in flight. Once everything has settled (InService /
    // NotFound / Failed, EC2 running or stopped) there is nothing to watch, and
    // 30s cuts steady-state load by 6x. A user action invalidates this query
    // anyway (see onSuccess below), so the slow interval never delays feedback
    // from clicking Start or Stop.
    refetchInterval: (query) =>
      isTransitioning(query.state.data) ? POLL_TRANSITIONING_MS : POLL_SETTLED_MS,
  });
}

export function useVoiceSettings() {
  const authFetch = useAuthFetch();
  return useQuery<VoiceSettings>({
    queryKey: ['voice', 'settings'],
    queryFn: async () => {
      const res = await authFetch('/v1/voice/settings');
      if (!res.ok) throw new Error('Failed to fetch voice settings');
      return res.json();
    },
  });
}

export function useUpdateVoiceSettings() {
  const authFetch = useAuthFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (inactivityMinutes: number): Promise<VoiceSettings> => {
      const res = await authFetch('/v1/voice/settings', {
        method: 'PUT',
        body: JSON.stringify({ inactivityMinutes }),
      });
      if (!res.ok) throw new Error('Failed to update voice settings');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['voice', 'settings'] }),
  });
}

export function useStartVoice() {
  const authFetch = useAuthFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (inactivityMinutes: number) => {
      const res = await authFetch('/v1/voice/start', {
        method: 'POST',
        body: JSON.stringify({ inactivityMinutes }),
      });
      if (!res.ok) throw new Error('Failed to start voice services');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['voice', 'status'] }),
  });
}

export function useStopVoice() {
  const authFetch = useAuthFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await authFetch('/v1/voice/stop', { method: 'POST' });
      if (!res.ok) throw new Error('Failed to stop voice services');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['voice', 'status'] }),
  });
}

export function usePersonas() {
  const authFetch = useAuthFetch();
  return useQuery<Persona[]>({
    queryKey: ['voice', 'personas'],
    queryFn: async () => {
      const res = await authFetch('/v1/voice/personas');
      if (!res.ok) throw new Error('Failed to fetch personas');
      return res.json();
    },
  });
}

export function useVoiceToken() {
  const authFetch = useAuthFetch();
  return useMutation({
    mutationFn: async ({ personaId, callerName }: { personaId: string; callerName?: string }): Promise<TokenResponse> => {
      const res = await authFetch('/v1/voice/token', {
        method: 'POST',
        body: JSON.stringify({ personaId, callerName }),
      });
      if (!res.ok) throw new Error('Failed to get voice token');
      return res.json();
    },
  });
}

export function useVoiceSampleUploadUrl() {
  const authFetch = useAuthFetch();
  return useMutation({
    mutationFn: async (voiceId: string): Promise<UploadTicket> => {
      const res = await authFetch('/v1/voice/voices/upload-url', {
        method: 'POST',
        body: JSON.stringify({ voiceId }),
      });
      if (!res.ok) throw new Error('Failed to prepare voice upload');
      return res.json();
    },
  });
}

export function useAvatarUploadUrl() {
  const authFetch = useAuthFetch();
  return useMutation({
    mutationFn: async ({ personaId, contentType }: { personaId: string; contentType: string }): Promise<UploadTicket> => {
      const res = await authFetch(`/v1/voice/personas/${personaId}/avatar-upload-url`, {
        method: 'POST',
        body: JSON.stringify({ contentType }),
      });
      if (!res.ok) throw new Error('Failed to prepare avatar upload');
      return res.json();
    },
  });
}

export function useStartTranscription() {
  const authFetch = useAuthFetch();
  return useMutation({
    mutationFn: async ({ voiceId, language }: { voiceId: string; language: string }): Promise<TranscriptionJob> => {
      const res = await authFetch(`/v1/voice/voices/${voiceId}/transcriptions`, {
        method: 'POST',
        body: JSON.stringify({ language }),
      });
      if (!res.ok) throw new Error('Failed to start transcription');
      return res.json();
    },
  });
}

export function useTranscriptionStatus() {
  const authFetch = useAuthFetch();
  return useMutation({
    mutationFn: async (jobId: string): Promise<TranscriptionJob> => {
      const res = await authFetch(`/v1/voice/transcriptions/${jobId}`);
      if (!res.ok) throw new Error('Failed to check transcription');
      return res.json();
    },
  });
}

export function useEnrollPersona() {
  const authFetch = useAuthFetch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      id: string;
      name: string;
      language: string;
      transcript: string;
      systemPrompt: string;
      imageUrl?: string;
    }) => {
      const res = await authFetch('/v1/voice/personas/enroll', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => null))?.error ?? 'Failed to start persona enrollment');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['voice', 'personas'] }),
  });
}

export function usePersonaEnrollmentStatus() {
  const authFetch = useAuthFetch();
  return useMutation({
    mutationFn: async (personaId: string): Promise<Persona> => {
      const res = await authFetch(`/v1/voice/personas/${personaId}`);
      if (!res.ok) throw new Error('Failed to check voice enrollment status');
      return res.json();
    },
  });
}
